/* UGC video carousel (sections/ugc-video-carousel.liquid).
   One <ugc-video-carousel> element per section, so copies do not conflict.
   - Cards scroll sideways with native scrolling and scroll snap.
   - Uploaded videos preview muted and looped while at least 60% of the card
     is in view, and pause when it leaves.
   - A tap (or click, or Enter/Space) on a card opens a <dialog>: uploaded
     videos play from the start with sound and native controls; Instagram-only
     cards show the embed. A swipe does not open it: if the pointer moves more
     than 10px between pointerdown and pointerup, the click that follows is
     ignored. Pointer events cover touch and mouse with the same code.
   - The pop-up video gets <source> elements in this order: HLS (m3u8), the
     720p MP4, the highest MP4 (data attributes on the card button).
   - The dialog closes with its close button, a tap on the backdrop or Escape.
     On close the video pauses, goes back to the start and loses its sources
     (so it stops downloading), page scroll is unlocked and focus returns to
     the card that opened it.
   - Instagram's script is loaded the first time an Instagram pop-up opens,
     never on page load.
   - Arrows scroll the track and disable at the start and end.
   - With prefers-reduced-motion: no autoplay and no smooth scrolling. */
(() => {
  if (customElements.get('ugc-video-carousel')) return;

  const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');
  const INSTAGRAM_SCRIPT = 'https://www.instagram.com/embed.js';
  const TAP_TOLERANCE = 10;
  let instagramScript = null;

  // Load Instagram's embed script once for the whole page.
  function loadInstagram() {
    if (window.instgrm) return Promise.resolve();
    if (!instagramScript) {
      instagramScript = new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = INSTAGRAM_SCRIPT;
        script.async = true;
        script.onload = resolve;
        script.onerror = reject;
        document.body.appendChild(script);
      });
    }
    return instagramScript;
  }

  // https://www.instagram.com/reel/ABC/?igsh=... -> https://www.instagram.com/reel/ABC/
  function cleanPermalink(url) {
    try {
      const parsed = new URL(url);
      return `${parsed.origin}${parsed.pathname.endsWith('/') ? parsed.pathname : `${parsed.pathname}/`}`;
    } catch (error) {
      return url;
    }
  }

  class UgcVideoCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-ugc-track]');
      this.prev = this.querySelector('[data-ugc-prev]');
      this.next = this.querySelector('[data-ugc-next]');
      this.dialog = this.querySelector('[data-ugc-dialog]');
      this.embed = this.querySelector('[data-ugc-embed]');
      this.modalVideo = this.querySelector('[data-ugc-modal-video]');
      this.triggers = Array.from(this.querySelectorAll('[data-ugc-open], [data-ugc-instagram]'));
      this.previews = Array.from(this.querySelectorAll('[data-ugc-open]')).map((trigger) => ({
        trigger,
        card: trigger.closest('[data-ugc-card]'),
        video: trigger.querySelector('[data-ugc-video]'),
        inView: false,
      }));
      this.lastTrigger = null;
      this.isOpen = false;
      this.autoplay = this.dataset.autoplay !== 'false';
      this.scrollLock = null;

      this.onScroll = this.onScroll.bind(this);
      this.onResize = this.updateArrows.bind(this);
      this.ticking = false;

      this.setUpPreviews();
      this.setUpTriggers();
      this.setUpArrows();
      this.setUpDialog();
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      if (this.track) this.track.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onResize);
      this.unlockScroll();
    }

    // ---------- Previews in the carousel ----------

    canAutoplay() {
      return this.autoplay && !REDUCED_MOTION.matches && !(this.dialog && this.dialog.open);
    }

    setUpPreviews() {
      this.previews.forEach((preview) => {
        this.setPaused(preview, true);
        preview.video.addEventListener('play', () => this.setPaused(preview, false));
        preview.video.addEventListener('pause', () => this.setPaused(preview, true));
      });

      if (this.previews.length === 0 || !('IntersectionObserver' in window)) return;
      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const preview = this.previews.find((item) => item.card === entry.target);
            if (!preview) return;
            preview.inView = entry.intersectionRatio >= 0.6;
            if (preview.inView) {
              if (this.canAutoplay()) this.playPreview(preview);
            } else if (!preview.video.paused) {
              preview.video.pause();
            }
          });
        },
        { threshold: [0, 0.6] }
      );
      this.previews.forEach((preview) => this.observer.observe(preview.card));
    }

    playPreview(preview) {
      const attempt = preview.video.play();
      if (attempt && typeof attempt.catch === 'function') {
        attempt.catch(() => this.setPaused(preview, true));
      }
    }

    setPaused(preview, paused) {
      preview.card.classList.toggle('is-paused', paused);
    }

    pauseAllPreviews() {
      this.previews.forEach((preview) => {
        if (!preview.video.paused) preview.video.pause();
      });
    }

    resumePreviewsInView() {
      if (!this.canAutoplay()) return;
      this.previews.forEach((preview) => {
        if (preview.inView) this.playPreview(preview);
      });
    }

    // ---------- Tap versus swipe ----------

    setUpTriggers() {
      this.triggers.forEach((trigger) => {
        let start = null;
        let moved = false;

        trigger.addEventListener('pointerdown', (event) => {
          if (event.pointerType === 'mouse' && event.button !== 0) return;
          start = { x: event.clientX, y: event.clientY };
          moved = false;
        });

        trigger.addEventListener('pointermove', (event) => {
          if (!start || moved) return;
          if (Math.hypot(event.clientX - start.x, event.clientY - start.y) > TAP_TOLERANCE) moved = true;
        });

        trigger.addEventListener('pointerup', (event) => {
          if (start && Math.hypot(event.clientX - start.x, event.clientY - start.y) > TAP_TOLERANCE) moved = true;
          start = null;
        });

        // The browser took over the gesture (a scroll or swipe).
        trigger.addEventListener('pointercancel', () => {
          moved = true;
          start = null;
        });

        // The modal opens on click: it follows a tap, a mouse click or
        // Enter/Space, and counts as a user gesture, so the video may play
        // with sound. A click that ends a swipe is ignored.
        trigger.addEventListener('click', (event) => {
          // detail is 0 for Enter/Space, which never ends a swipe.
          if (moved && event.detail !== 0) {
            moved = false;
            event.preventDefault();
            return;
          }
          moved = false;
          if (trigger.hasAttribute('data-ugc-open')) {
            this.openVideo(trigger);
          } else {
            this.openInstagram(trigger);
          }
        });
      });
    }

    // ---------- Arrows ----------

    setUpArrows() {
      if (!this.track) return;
      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onResize);
      if (this.prev) this.prev.addEventListener('click', () => this.scrollByCards(-1));
      if (this.next) this.next.addEventListener('click', () => this.scrollByCards(1));
      this.updateArrows();
    }

    onScroll() {
      if (this.ticking) return;
      this.ticking = true;
      window.requestAnimationFrame(() => {
        this.ticking = false;
        this.updateArrows();
      });
    }

    // Scroll by as many whole cards as fit in the visible track.
    scrollByCards(direction) {
      const card = this.track.querySelector('[data-ugc-card]');
      if (!card) return;
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      const step = card.getBoundingClientRect().width + gap;
      const count = Math.max(1, Math.floor(this.track.clientWidth / step) - 1);
      this.track.scrollBy({
        left: direction * step * count,
        behavior: REDUCED_MOTION.matches ? 'auto' : 'smooth',
      });
    }

    updateArrows() {
      if (!this.prev || !this.next) return;
      const max = this.track.scrollWidth - this.track.clientWidth;
      this.prev.disabled = this.track.scrollLeft <= 1;
      this.next.disabled = this.track.scrollLeft >= max - 1;
    }

    // ---------- Pop-up ----------

    setUpDialog() {
      if (!this.dialog) return;

      this.querySelector('[data-ugc-close]').addEventListener('click', () => this.closeDialog());

      // A click on the backdrop (or the dark area around the video) reaches
      // the dialog element itself.
      this.dialog.addEventListener('click', (event) => {
        if (event.target === this.dialog) this.closeDialog();
      });

      // Escape: close and tidy up straight away, like the button and backdrop.
      // The close event (which arrives a moment later) is a backstop for any
      // other way the dialog closes.
      this.dialog.addEventListener('cancel', (event) => {
        event.preventDefault();
        this.closeDialog();
      });
      this.dialog.addEventListener('close', () => this.onClosed());
    }

    closeDialog() {
      if (this.dialog.open) this.dialog.close();
      this.onClosed();
    }

    // Runs once per opening, whichever way the dialog was closed.
    onClosed() {
      if (!this.isOpen) return;
      this.isOpen = false;
      // Pause, go back to the start and remove the sources, so nothing keeps
      // downloading after the pop-up has closed.
      this.clearVideo();
      this.embed.innerHTML = '';
      this.dialog.classList.remove('ugc__dialog--video', 'ugc__dialog--instagram');
      this.unlockScroll();
      this.resumePreviewsInView();
      if (this.lastTrigger && document.contains(this.lastTrigger)) {
        this.lastTrigger.focus({ preventScroll: true });
      }
    }

    lockScroll() {
      if (this.scrollLock) return;
      const root = document.documentElement;
      this.scrollLock = { root: root.style.overflow, body: document.body.style.overflow };
      root.style.overflow = 'hidden';
      document.body.style.overflow = 'hidden';
    }

    unlockScroll() {
      if (!this.scrollLock) return;
      document.documentElement.style.overflow = this.scrollLock.root;
      document.body.style.overflow = this.scrollLock.body;
      this.scrollLock = null;
    }

    showDialog(type) {
      this.dialog.classList.add(`ugc__dialog--${type}`);
      this.pauseAllPreviews();
      this.lockScroll();
      this.dialog.showModal();
      this.isOpen = true;
    }

    // The pop-up's sources, best first: HLS stream, 720p MP4, highest MP4.
    // The browser plays the first type it supports (most desktop browsers
    // skip HLS and use the 720p MP4).
    videoSources(trigger) {
      const { ugcHls, ugcMp4, ugcMp4Hd } = trigger.dataset;
      return [
        ugcHls && { src: ugcHls, type: 'application/x-mpegURL' },
        ugcMp4 && { src: ugcMp4, type: 'video/mp4' },
        ugcMp4Hd && { src: ugcMp4Hd, type: 'video/mp4' },
      ].filter(Boolean);
    }

    // Remove every source and reload, so the browser stops downloading.
    clearVideo() {
      const video = this.modalVideo;
      if (!video) return;
      video.pause();
      try {
        video.currentTime = 0;
      } catch (error) {
        // Nothing loaded yet: already at the start.
      }
      video.querySelectorAll('source').forEach((source) => source.remove());
      video.removeAttribute('src');
      video.removeAttribute('poster');
      video.load();
    }

    openVideo(trigger) {
      const sources = this.videoSources(trigger);
      if (sources.length === 0 || !this.modalVideo) return;
      if (typeof this.dialog.showModal !== 'function') {
        window.open(sources[sources.length - 1].src, '_blank', 'noopener');
        return;
      }
      this.lastTrigger = trigger;

      const video = this.modalVideo;
      this.clearVideo();
      sources.forEach(({ src, type }) => {
        const source = document.createElement('source');
        source.src = src;
        source.type = type;
        video.appendChild(source);
      });
      if (trigger.dataset.ugcPoster) video.setAttribute('poster', trigger.dataset.ugcPoster);
      video.muted = false;
      video.setAttribute('aria-label', trigger.getAttribute('aria-label').replace(/^Play /, ''));
      video.load();

      this.showDialog('video');

      // Called inside the tap, so the browser allows sound.
      const attempt = video.play();
      if (attempt && typeof attempt.catch === 'function') {
        attempt.catch(() => {
          // Autoplay with sound refused: the native controls are there to start it.
        });
      }
    }

    openInstagram(trigger) {
      if (typeof this.dialog.showModal !== 'function') {
        window.open(trigger.dataset.ugcInstagram, '_blank', 'noopener');
        return;
      }
      this.lastTrigger = trigger;

      const permalink = cleanPermalink(trigger.dataset.ugcInstagram);
      const quote = document.createElement('blockquote');
      quote.className = 'instagram-media';
      quote.setAttribute('data-instgrm-permalink', permalink);
      quote.setAttribute('data-instgrm-version', '14');
      const fallback = document.createElement('a');
      fallback.href = permalink;
      fallback.target = '_blank';
      fallback.rel = 'noopener';
      fallback.textContent = 'View this post on Instagram';
      quote.appendChild(fallback);

      this.embed.innerHTML = '';
      this.embed.appendChild(quote);
      this.showDialog('instagram');

      loadInstagram()
        .then(() => {
          if (window.instgrm && window.instgrm.Embeds) window.instgrm.Embeds.process();
        })
        .catch(() => {
          // The fallback link inside the quote still works.
        });
    }
  }

  customElements.define('ugc-video-carousel', UgcVideoCarousel);
})();
